<template>
    <div>
        <div v-if="marketsList" class="d-flex overflow-auto" style="min-width:300px;">
            <div v-if="searchKeyword" :style="widthStyle">
                <div v-if="numberSelectedMarkets || numberSelectedCities" class="small text-muted pb-2">
                    <span v-if="numberSelectedMarkets" class="me-2">
                        {{ numberSelectedMarkets }} {{ pluralize(numberSelectedMarkets, 'Markets', 'Market') }}
                    </span>
                    <span v-if="numberSelectedCities" class="me-2">
                        {{ numberSelectedCities }} {{ pluralize(numberSelectedCities, 'Towns', 'Town') }}
                    </span>
                </div>
                <div class="max-vh-50 overflow-auto">
                    <div v-if="searchMarketsList" class="position-relative mb-3">
                        <h4 class="sticky-top bg-body" :class="uiStore.isMobile ? 'ps-3' : null">Markets</h4>

                        <list-group :flush="true">
                            <list-group-item v-for="(market, index) in searchMarketsList" :key="`properties-keyword-regions-markets-${index}`" :action="() => toggleMarket(market)" :active="selectedMarkets[market.id] ? true : false">
                                {{ market.name }}
                            </list-group-item>
                        </list-group>
                    </div>

                    <div v-if="searchCitiesList" class="position-relative mb-3">
                        <h4 class="sticky-top bg-body" :class="uiStore.isMobile ? 'ps-3' : null">Towns</h4>

                        <list-group :flush="true">
                            <list-group-item v-for="(city, index) in searchCitiesList" :key="`properties-keyword-regions-city-${index}`" :action="() => toggleCity(city)" :active="selectedCities[city.key] ? true : false">
                                {{ city.name }}
                            </list-group-item>
                            
                        </list-group>
                    </div>

                    <div v-if="searchPropertiesList" class="position-relative mb-3">
                        <h4 class="sticky-top bg-body" :class="uiStore.isMobile ? 'ps-3' : null">Properties</h4>

                        <list-group :flush="true" >
                            <list-group-item v-for="(property, index) in searchPropertiesList" :key="`properties-keyword-regions-property-${index}`" :action="() => selectProperty(property)">
                                {{ property.n }}
                            </list-group-item>
                            
                        </list-group>
                    </div>
                </div>
            </div>
            <template v-else>
                <transition name="h-slide-transition-4">
                    <div v-if="showMarkets" :class="uiStore.isMobile ? 'w-100' : null">
                        <div v-if="uiStore.isMobile" class="x-small spaced-letters text-uppercase px-3 pb-3 d-flex align-items-center">
                            <div class="clickable text-primary me-4" @click="showCities=false">
                                Markets
                            </div>
                            <div class="clickable text-muted" @click="showCities=true">
                                Towns
                            </div>
                        </div>
                        <h4 v-else>Markets</h4>
                        <div class="max-vh-50 overflow-auto me-md-3" :style="widthStyle" ref="marketsList">
                            <list-group :flush="true">
                                <list-group-item v-for="(market, index) in marketsList" :key="`properties-search-regions-markets-${index}`" :action="() => toggleMarket(market)" :active="selectedMarkets[market.id] ? true : false">
                                    {{ market.name }}
                                </list-group-item>
                            </list-group>
                        </div>
                    </div>
                </transition>
                <transition name="h-slide-transition-4">
                    <div v-if="showMarketCities && marketCitiesList" :class="uiStore.isMobile ? 'w-100' : null">
                        <div v-if="uiStore.isMobile" class="x-small spaced-letters text-uppercase px-3 pb-3 d-flex align-items-center">
                            <div class="clickable text-muted me-4" @click="showCities=false">
                                Markets
                            </div>
                            <div class="clickable text-primary" @click="showCities=true">
                                Towns
                            </div>
                        </div>
                        <h4 v-else>Towns</h4>
                        <div class="max-vh-50 overflow-auto" :style="widthStyle" ref="citiesList">
                            <list-group :flush="true">
                                <list-group-item v-for="(city, index) in marketCitiesList" :key="`properties-search-regions-city-${index}`" :action="() => toggleCity(city)" :active="selectedCities[city.key] ? true : false">
                                    {{ city.name }}
                                </list-group-item>
                            </list-group>
                        </div>
                    </div>
                </transition>
            </template>
        </div>

        <div class="d-flex align-items-center justify-content-end mt-3" :class="uiStore.isMobile ? 'p-3' : null">
            <ui-button v-if="uiStore.isMobile" size="sm" color="light" @click="$emit('close')" class="me-auto">
                Close
            </ui-button>
            <template v-else>
                <ui-button v-if="showCitiesButton" size="sm" color="light" @click="showCities=!showCities" class="me-auto">
                    {{ showCities ? 'Hide Towns' : 'Show Towns' }}
                </ui-button>
                <ui-button v-else-if="showMarketsButton" size="sm" color="light" @click="showCities=false" class="me-auto">
                    Return to Markets
                </ui-button>
            </template>
            <div>
                <ui-button size="sm" color="light" @click="clear" class="me-2">
                    Clear
                </ui-button>

                <ui-button size="sm" @click="apply">
                    Apply
                </ui-button>
            </div>
        </div>
    </div>
    
</template>

<script type="text/javascript">
    import CommonComponents from '@/components/Common.vue';

    import UI from '@/classes/UI.js';
    import Formatter from '@/classes/Formatter.js';
    import ApiObject from '@/classes/ApiObject.js';
    import { UseUIStore } from '@/store/UI.js';

    export default {
        components: {
            ...CommonComponents,
        },

        setup() {
            return {
                uiStore: UseUIStore(),
            };
        },

        data() {
            return {
                selectedMarkets: {},
                selectedCities: {},

                showCities: false,
            };
        },

        emits: ['close', 'clear', 'update:modelValue'],

        created() {
            this.setSearchData();

            this.$watch(
                () => this.modelValue,
                this.setSearchData,
                { deep: true }
            );
        },

        props: {
            modelValue: {
                type: Object,
                default: null,
            },

            searchKeyword: {
                type: String,
            },

            searchData: {
                type: ApiObject,
                required: true,
            },

            marketsList: {
                type: Array,
                required: true,
            },

            // a pre-computed list of properties
            /**
             * IMPORTANT, the properties in this response
             * will have truncated keys
             * e.g. instead of "name", it'll be "n"
             * 'name'                  =>  'n',
             * 'slug'                  =>  'sg',
             * 'city_state_country'    =>  'c',
             * 'market_id'             =>  'mId',
             * 'bedrooms'              =>  'bd',
             * 'bathrooms'             =>  'bt',
             * 'service_level'         =>  'sl',
             * 'max_occupancy'         =>  'oc',
             * 'schedule_price_avg'    =>  'avg',
             * 'public_lat'            =>  'lt',
             * 'public_lng'            =>  'lg',
             * 'pet_policy'            =>  'pt',
             */
            properties: {
                type: Array,
                required: true,
            },
        },
        
        watch: {
            searchKeyword(newVal, oldVal) {
                if ((oldVal) && (!newVal)) {
                    requestAnimationFrame(function() {

                        if (this.$refs.marketsList) {
                            let active = this.$refs.marketsList.querySelector('.active');
                            if (active) {
                                UI.scrollToChild(this.$refs.marketsList, active);
                            }
                        }

                        if (this.$refs.citiesList) {
                            let active = this.$refs.citiesList.querySelector('.active');
                            if (active) {
                                UI.scrollToChild(this.$refs.citiesList, active);
                            }
                        }
                        
                    }.bind(this));
                }
            }
        },

        computed: {

            citiesList() {
                if (this.properties) {
                    // keep track of cities that were added
                    let added = {};

                    let cities = [];

                    for (let i=0; i<this.properties.length; i++) {

                        let key = this.properties[i].c.toLowerCase();

                        if (!added[key]) {
                            cities.push({
                                name: this.properties[i].c,
                                marketId: this.properties[i].mId,
                                key: key,
                            });

                            added[key] = true;
                        }
                    }

                    cities.sort(function(a, b) {
                        let aName = a.name.toUpperCase();
                        let bName = b.name.toUpperCase();

                        if (aName < bName) {
                            return -1;
                        }
                        if (aName > bName) {
                            return 1;
                        }
                        return 0;
                    });

                    return cities;
                }
                return null;
            },

            showMarketCities() {
                if (this.uiStore.isMobile) {
                    return this.showCities;
                }

                if ((this.showCities) || (Object.keys(this.selectedMarkets).length)) {
                    return true;
                }

                return false;
            },

            marketCitiesList() {
                let marketIds = Object.keys(this.selectedMarkets);

                let cities = [];
                for (let i=0; i<this.citiesList.length; i++) {
                    if ((marketIds.length == 0) || (marketIds.indexOf(this.citiesList[i].marketId.toString()) != -1)) {
                        cities.push(this.citiesList[i]);
                    }
                }

                if (cities.length) {
                    return cities;
                }

                return null;
            },

            searchMarketsList() {
                if (!this.searchKeyword) return;
                let keyword = this.searchKeyword.toLowerCase();

                let markets = [];
                if (this.marketsList) {
                    for (let i=0; i<this.marketsList.length; i++) {
                        if (this.marketsList[i].name.toLowerCase().indexOf(keyword) != -1) {
                            markets.push(this.marketsList[i]);
                        }
                    }
                }

                if (markets.length) {
                    return markets;
                }
                return null;
            },

            searchCitiesList() {
                if (!this.searchKeyword) return;
                let keyword = this.searchKeyword.toLowerCase();

                let cities = [];
                if (this.marketCitiesList) {
                    for (let i=0; i<this.marketCitiesList.length; i++) {
                        if (this.marketCitiesList[i].name.toLowerCase().indexOf(keyword) != -1) {
                            cities.push(this.marketCitiesList[i]);
                        }
                    }
                }

                if (cities.length) {
                    return cities;
                }
                return null;
            },

            searchPropertiesList() {
                if (!this.searchKeyword) return;
                let keyword = this.searchKeyword.toLowerCase();
                let marketIds = Object.keys(this.selectedMarkets);
                let cityKeys = Object.keys(this.selectedCities);

                let properties = [];
                if (this.properties) {
                    for (let i=0; i<this.properties.length; i++) {
                        
                        if ((marketIds.length) && (marketIds.indexOf(this.properties[i].mId.toString()) == -1)) {
                            continue;
                        }

                        if ((cityKeys.length) && (cityKeys.indexOf(this.properties[i].c.toLowerCase()) == -1)) {
                            continue;
                        }

                        if (this.properties[i].n.toLowerCase().indexOf(keyword) != -1) {
                            properties.push(this.properties[i]);
                        }
                    }
                }

                if (properties.length) {
                    return properties;
                }
                return null; 
            },


            showCitiesButton() {
                if (this.searchKeyword) {
                    return false;
                }

                if (this.uiStore.isMobile) {
                    return !this.showCities;
                }

                if (Object.keys(this.selectedMarkets).length == 0) {
                    return true;
                }

                return false;
            },

            showMarketsButton() {
                if (this.uiStore.isMobile) {
                    return this.showCities;
                }

                return false;
            },

            showMarkets() {
                if (this.uiStore.isMobile) {
                    return !this.showCities;
                }
                return true;
            },

            numberSelectedMarkets() {
                if (this.selectedMarkets) {
                    return Object.keys(this.selectedMarkets).length;
                }
                return 0;
            },

            numberSelectedCities() {
                if (this.selectedCities) {
                    return Object.keys(this.selectedCities).length;
                }
                return 0;
            },

            widthStyle() {
                if (!this.uiStore.isMobile) {
                    return 'width: fit-content; min-width: 300px;';
                }
                return 'min-width: 300px;';
            },
        },

        methods: {

            pluralize(count, plural, single) {
                return Formatter.pluralize(count, plural, single);
            },

            setSearchData() {

                this.selectedMarkets =  {};
                this.selectedCities = {};

                if (!this.modelValue) {
                    return;
                }

                let newMarkets = {};
                let newCities = {};

                if ((this.modelValue.markets) && (this.modelValue.markets.length)) {
                    // we'll key the markets by id so we can toggle the selected
                    // markets using a hash map
                    let marketsObj = {};
                    
                    let marketsList = this.marketsList;
                    for (let i=0; i<marketsList.length; i++) {
                        let id = marketsList[i].id;
                        marketsObj[id] = marketsList[i];
                    }

                    for (let i=0; i<this.modelValue.markets.length; i++) {
                        if (marketsObj[this.modelValue.markets[i]]) {
                            newMarkets[this.modelValue.markets[i]] = marketsObj[this.modelValue.markets[i]];
                        }
                    }
                }

                this.selectedMarkets = newMarkets;

                if ((this.modelValue.cities) && (this.modelValue.cities.length)) {
                    let citiesObj = {};
                    let citiesList = this.marketCitiesList;

                    for (let i=0; i<citiesList.length; i++) {
                        let key = citiesList[i].key;
                        citiesObj[key] = citiesList[i];
                    }

                    for (let i=0; i<this.modelValue.cities.length; i++) {
                        let key = this.modelValue.cities[i].toLowerCase();
                        if (citiesObj[key]) {
                            newCities[key] = citiesObj[key];
                        }
                    }
                }

                this.selectedCities = newCities;
            },

            toggleMarket(market) {
                if (this.selectedMarkets[market.id]) {
                    delete this.selectedMarkets[market.id];
                    
                    let citiesToRemove = [];
                    for (const [key, city] of Object.entries(this.selectedCities)) {
                        if (city.marketId == market.id) {
                            citiesToRemove.push(key);
                        }
                    }

                    for (let i=0; i<citiesToRemove.length; i++) {
                        delete this.selectedCities[citiesToRemove[i]];
                    }
                }
                else {
                    this.selectedMarkets[market.id] = market;
                    this.showCities = false;
                }

                if (!this.uiStore.isMobile) {
                    this.emitUpdate();
                }
            },

            toggleCity(city) {
                if (this.selectedCities[city.key]) {
                    delete this.selectedCities[city.key];
                }
                else {
                    this.selectedCities[city.key] = city;
                    if (this.searchKeyword) {
                        this.showCities = true;
                    }
                }

                if (!this.uiStore.isMobile) {
                    this.emitUpdate();
                }
            },

            selectProperty(property) {
                this.$router.push('/properties/'+property.sg);
            },

            clear() {
                this.selectedMarkets = {};
                this.selectedCities = {};
                this.showCities = false;
                this.$emit('clear');
                this.emitUpdate();
            },

            emitUpdate() {
                let cities = [];
                let selectedCities = Object.values(this.selectedCities);
                for (let i=0; i<selectedCities.length; i++) {
                    cities.push(selectedCities[i].name);
                }

                // we need to convert the market ids to integer
                let markets = Object.values(this.selectedMarkets);
                let marketIds = [];
                let marketNames = [];
                for (let i=0; i<markets.length; i++) {
                    marketIds.push(markets[i].id);
                    marketNames.push(markets[i].name);
                }
                let data = {
                    markets: marketIds,
                    cities: cities,
                    marketNames: marketNames,
                };

                this.$emit('update:modelValue', data);
            },

            apply() {
                this.emitUpdate();
                this.$emit('close');
            },
        }

    };

</script>
